import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { runSetupDiagnostics, SEVERITY_LABELS, type Diagnostic, type Severity } from '../utils/setupDiagnostics';
import { evaluatePlan, PLAN_LABELS } from '../utils/planGate';

/**
 * 開店前チェック。
 *
 * 「エラーは出ないが、結果が空になる／おかしくなる」設定漏れを名指しで出す。
 * 実店舗のデータで確かめたところ、使えない状態が3件・確認が必要が4件見つかった。
 * どれも画面上はエラーが出ないので、使う側からは「バグだらけ」としか見えない。
 */

const ICONS: Record<Severity, React.ReactNode> = {
  blocker: <ErrorIcon color="error" />,
  warning: <WarningIcon sx={{ color: 'warning.main' }} />,
  info: <InfoIcon color="disabled" />,
};

const COLORS: Record<Severity, 'error' | 'warning' | 'default'> = {
  blocker: 'error',
  warning: 'warning',
  info: 'default',
};

/** 診断ID → 直しに行く画面 */
const FIX_ROUTES: Record<string, { path: string; label: string }> = {
  'legacy-staff': { path: '/employees', label: '従業員管理へ' },
  'employee-no-name': { path: '/employees', label: '従業員管理へ' },
  'employee-no-wage': { path: '/employees', label: '従業員管理へ' },
  'employee-no-login': { path: '/employees', label: '従業員管理へ' },
  // あとから足した3つに導線を付け忘れていた。
  // 新規の店でいちばん出やすい項目なのに、直す場所が出ていなかった。
  'no-products': { path: '/mobile-order/products', label: '商品管理へ' },
  'no-categories': { path: '/mobile-order/categories', label: 'カテゴリ管理へ' },
  'no-employees': { path: '/employees', label: '従業員管理へ' },
  'no-cost': { path: '/mobile-order/products', label: '商品管理へ' },
  'partial-cost': { path: '/mobile-order/products', label: '商品管理へ' },
  'no-price': { path: '/mobile-order/products', label: '商品管理へ' },
  'no-category': { path: '/mobile-order/products', label: '商品管理へ' },
  'no-category-distribution': { path: '/mobile-order/categories', label: 'カテゴリ管理へ' },
  'backrate-100': { path: '/settings/shop', label: '店舗設定へ' },
  'no-progressive-mode': { path: '/settings/shop', label: '店舗設定へ' },
  'no-standard-hours': { path: '/settings/shop', label: '店舗設定へ' },
  'no-boundary': { path: '/settings/shop', label: '店舗設定へ' },
  'no-closing-day': { path: '/settings/shop', label: '店舗設定へ' },
  'no-attendance-settings': { path: '/settings/shop', label: '店舗設定へ' },
  'no-payment-method': { path: '/settings/shop', label: '店舗設定へ' },
  'no-tables': { path: '/mobile-order/tables', label: 'テーブル管理へ' },
  'no-printer': { path: '/mobile-order/printers', label: 'プリンター管理へ' },
};

const SetupCheck: React.FC = () => {
  const { shop } = useAuth();
  const navigate = useNavigate();
  const [diags, setDiags] = useState<Diagnostic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState({
    slipsThisMonth: 0, peakMonthlySlips: 0, employeeCount: 0, shopCount: 1,
  });
  // レガシー台帳の統合
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergePlan, setMergePlan] = useState<any | null>(null);
  const [merging, setMerging] = useState(false);
  const [mergeDone, setMergeDone] = useState<any | null>(null);

  const run = useCallback(async () => {
    if (!shop) return;
    setLoading(true);
    setError(null);
    try {
      const byShop = async (c: string) => {
        const s = await getDocs(query(collection(db, c), where('shopId', '==', shop.id)));
        return s.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      };
      const [shopDoc, employees, products, categories, tables, printers, legacyStaff] =
        await Promise.all([
          getDoc(doc(db, 'shops', shop.id)),
          byShop('employees'), byShop('products'), byShop('productCategories'),
          byShop('tables'), byShop('printers'), byShop('staff'),
        ]);

      setDiags(runSetupDiagnostics({
        shop: (shopDoc.data() ?? {}) as any,
        employees, products, categories, tables, printers,
        // 統合済み（mergedInto あり）は数えない。直したのに件数が減らないと直った気がしない。
        legacyStaffCount: legacyStaff.filter((x: any) => !x.mergedInto).length,
      }));

      // 課金は伝票数で決まる。当月と直近3ヶ月のピークを数える。
      const now = new Date();
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const slipSnap = await getDocs(query(
        collection(db, 'checkouts'),
        where('shopId', '==', shop.id),
        where('createdAt', '>=', Timestamp.fromDate(threeMonthsAgo)),
      ));
      const byMonth: Record<string, number> = {};
      slipSnap.docs.forEach((d) => {
        const t = (d.data() as any).createdAt?.toDate?.();
        if (!t) return;
        const key = `${t.getFullYear()}-${t.getMonth()}`;
        byMonth[key] = (byMonth[key] ?? 0) + 1;
      });
      const thisKey = `${now.getFullYear()}-${now.getMonth()}`;

      setUsage({
        slipsThisMonth: byMonth[thisKey] ?? 0,
        peakMonthlySlips: Math.max(0, ...Object.values(byMonth)),
        employeeCount: employees.filter((e: any) => e.status === 'active').length,
        shopCount: 1,
      });
    } catch (e: any) {
      setError(e?.message ?? '診断に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shop]);

  useEffect(() => { run(); }, [run]);

  /** どの人がどの人に統合されるかを、実行前に見せる */
  const previewMerge = async () => {
    if (!shop) return;
    setMerging(true);
    setMergeDone(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'asia-northeast1'), 'mergeLegacyStaff');
      const res: any = await fn({ shopId: shop.id, dryRun: true });
      setMergePlan(res.data);
      setMergeOpen(true);
    } catch (e: any) {
      setError(e?.message ?? '統合の下見に失敗しました');
    } finally {
      setMerging(false);
    }
  };

  const executeMerge = async () => {
    if (!shop) return;
    setMerging(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'asia-northeast1'), 'mergeLegacyStaff');
      const res: any = await fn({ shopId: shop.id, dryRun: false });
      setMergeDone(res.data);
      setMergePlan(null);
      await run();
    } catch (e: any) {
      setError(e?.message ?? '統合に失敗しました');
    } finally {
      setMerging(false);
    }
  };

  const blockers = diags.filter((d) => d.severity === 'blocker');
  const warnings = diags.filter((d) => d.severity === 'warning');
  const infos = diags.filter((d) => d.severity === 'info');
  const gate = evaluatePlan((shop as any)?.planTier, usage);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">開店前チェック</Typography>
          <Typography variant="body2" color="text.secondary">
            エラーは出ないが結果が空になる設定漏れを、使う前に洗い出します
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={run} disabled={loading}>
          再チェック
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {blockers.length === 0 ? (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
              <AlertTitle>営業を始められます</AlertTitle>
              使えない状態の項目はありません。
              {warnings.length > 0 && ` 確認が必要な項目が ${warnings.length} 件あります。`}
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>このままでは正しく動きません（{blockers.length} 件）</AlertTitle>
              下の「使えない」の項目を先に直してください。エラー画面は出ませんが、
              金額が合わない・一覧が空になるといった形で表れます。
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">使えない</Typography>
                <Typography variant="h5" color={blockers.length ? 'error.main' : 'success.main'} fontWeight="bold">
                  {blockers.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">確認が必要</Typography>
                <Typography variant="h5" color={warnings.length ? 'warning.main' : 'text.primary'} fontWeight="bold">
                  {warnings.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">任意</Typography>
                <Typography variant="h5" fontWeight="bold">{infos.length}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Typography variant="caption" color="text.secondary">プラン（伝票数で決まる）</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: gate.overLimit ? 'warning.main' : 'success.main' }}>
                  {PLAN_LABELS[gate.tier]}
                </Typography>
                <Typography variant="caption" color="text.secondary">{gate.message}</Typography>
              </Box>
            </Stack>
          </Paper>

          {(['blocker', 'warning', 'info'] as Severity[]).map((sev) => {
            const list = diags.filter((d) => d.severity === sev);
            if (list.length === 0) return null;
            return (
              <Box key={sev} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {SEVERITY_LABELS[sev]}（{list.length}）
                </Typography>
                <Stack spacing={1}>
                  {list.map((d) => {
                    const route = FIX_ROUTES[d.id];
                    return (
                      <Paper key={d.id} sx={{ p: 2, borderLeft: '4px solid', borderColor: `${COLORS[sev] === 'default' ? 'grey' : COLORS[sev]}.main` }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box sx={{ pt: 0.3 }}>{ICONS[sev]}</Box>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="subtitle2" fontWeight="bold">{d.title}</Typography>
                              {d.detail && <Chip size="small" label={d.detail} />}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              <b>起きること:</b> {d.effect}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              <b>直し方:</b> {d.fix}
                            </Typography>
                          </Box>
                          {d.id === 'legacy-staff' ? (
                            <Button size="small" variant="contained" disabled={merging} onClick={previewMerge}>
                              統合する
                            </Button>
                          ) : route ? (
                            <Button size="small" variant="outlined" onClick={() => navigate(route.path)}>
                              {route.label}
                            </Button>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
          {mergeDone && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMergeDone(null)}>
              <AlertTitle>台帳を統合しました</AlertTitle>
              {mergeDone.plan.length} 名を統合し、売上・指名・インセンティブの参照を付け替えました
              （{Object.entries(mergeDone.rewritten).map(([k, v]) => `${k} ${v}件`).join(' / ')}）。
              {mergeDone.unresolved.length > 0 &&
                ` ${mergeDone.unresolved.length} 件は名前が無いため統合できませんでした。`}
            </Alert>
          )}
        </>
      )}

      <Dialog open={mergeOpen} onClose={() => !merging && setMergeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>スタッフ台帳を統合する</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>やること</AlertTitle>
            古い台帳（/staff）のスタッフを、名前で従業員（/employees）に突き合わせて1人にまとめます。
            売上・指名・インセンティブの記録も新しい ID へ付け替えます。
            古い台帳のデータは<strong>消さず</strong>、統合先の印を付けて残します。
          </Alert>

          {mergePlan && (
            <>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                統合される {mergePlan.plan.length} 名
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>古い台帳の名前</TableCell>
                    <TableCell>統合先</TableCell>
                    <TableCell>扱い</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mergePlan.plan.map((p: any) => (
                    <TableRow key={p.legacyId}>
                      <TableCell>{p.legacyName}</TableCell>
                      <TableCell>{p.targetName}</TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={p.action === 'matched' ? '既存の従業員へ' : '新しく従業員を作る'}
                          color={p.action === 'matched' ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {mergePlan.unresolved.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle>統合できない {mergePlan.unresolved.length} 件</AlertTitle>
                  {mergePlan.unresolved.map((u: any) => (
                    <div key={u.legacyId}>{u.legacyName || '(名前なし)'}: {u.reason}</div>
                  ))}
                  これらは古い台帳に残ります。名前を入れてからもう一度実行してください。
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)} disabled={merging}>やめる</Button>
          <Button variant="contained" onClick={() => { setMergeOpen(false); executeMerge(); }} disabled={merging}>
            この内容で統合する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetupCheck;
